import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Loader2, Plus, KeyRound, Copy, ShieldOff, AlertTriangle, ExternalLink, BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";

const SCOPE_GROUPS = [
  {
    kind: "read",
    title: "Read-only",
    hint: "Safe for most integrations — these keys cannot change your data.",
    scopes: [
      { id: "read:timesheets", label: "Read timesheets" },
      { id: "read:entries", label: "Read time entries" },
      { id: "read:pto", label: "Read PTO requests" },
      { id: "read:workers", label: "Read directory (workers)" },
      { id: "read:worksites", label: "Read worksites" },
      { id: "read:dimensions", label: "Read dimensions" },
      { id: "read:webhooks", label: "Read webhook subscriptions" },
      { id: "read:rates", label: "Read bill / pay rate cards" },
    ],
  },
  {
    kind: "write",
    title: "Write (mutations)",
    hint: "These scopes let the key change your data. Grant them only to integrations you fully trust.",
    scopes: [
      { id: "write:timesheets", label: "Submit timesheets on behalf of workers" },
      { id: "write:entries", label: "Create / update / delete time entries" },
      { id: "write:approvals", label: "Approve or reject submitted timesheets" },
      { id: "write:pto", label: "Create / update / cancel PTO requests" },
      { id: "write:workers", label: "Create / update / deactivate workers" },
      { id: "write:worksites", label: "Create / update / deactivate worksites" },
      { id: "write:dimensions", label: "Create / update / delete dimensions + values" },
      { id: "write:webhooks", label: "Manage webhook subscriptions" },
      { id: "write:rates", label: "Create / update / deactivate rate cards" },
    ],
  },
  {
    kind: "advanced",
    title: "Advanced",
    hint: "Super-scope — grants every read + write capability. Equivalent to a tenant admin on the API surface.",
    scopes: [
      { id: "admin:*", label: "Full admin access (all read + all write)" },
    ],
  },
];

// Flat lookup used when rendering a key row's scope badges.
const ALL_SCOPES = SCOPE_GROUPS.flatMap((g) => g.scopes);

export default function ApiKeysPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(null); // {id, key, name, scopes}
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/api-keys", { params: { include_revoked: true } });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    try {
      await api.post(`/tenant/api-keys/${k.id}/revoke`);
      toast.success(`Revoked ${k.name}`);
      setConfirmRevoke(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="api-keys-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Integrations</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">API keys</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Use API keys to pull approved timesheets, PTO, and directory data from your ERP,
            payroll system, or a custom integration. Keys authenticate against{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/api/v1/*</code>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="api-keys-open-docs">
            <a href="/api/v1/docs" target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-4 w-4" /> v1 API docs
            </a>
          </Button>
          <NewKey onCreated={(k) => { setReveal(k); load(); }} />
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>All API keys</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No API keys yet — click "New API key" to create one.</TableCell></TableRow>
              )}
              {items.map((k) => (
                <TableRow key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{k.masked_key}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <Badge key={s} variant="outline" className="rounded-full text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    {k.revoked_at ? (
                      <Badge variant="outline" className="rounded-full border-red-500/40 text-red-700">Revoked</Badge>
                    ) : k.expires_at && new Date(k.expires_at) < new Date() ? (
                      <Badge variant="outline" className="rounded-full border-amber-500/40 text-amber-700">Expired</Badge>
                    ) : (
                      <Badge className="rounded-full bg-success/15 text-success">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" asChild data-testid={`api-key-usage-${k.id}`}>
                        <Link to={`/app/admin/api-keys/${k.id}/usage`}>
                          <BarChart3 className="mr-1 h-3.5 w-3.5" /> Usage
                        </Link>
                      </Button>
                      {!k.revoked_at && (
                        <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(k)} data-testid={`api-key-revoke-${k.id}`}>
                          <ShieldOff className="mr-1 h-3.5 w-3.5" /> Revoke
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RevealKeyDialog data={reveal} onClose={() => setReveal(null)} />
      <RevokeDialog k={confirmRevoke} onClose={() => setConfirmRevoke(null)} onConfirm={revoke} />
    </div>
  );
}

function NewKey({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["read:timesheets"]);
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [adminAck, setAdminAck] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setScopes(["read:timesheets"]); setExpiresAt("");
      setErr(""); setAdminAck(false);
    }
  }, [open]);

  const toggleScope = (id) => {
    setScopes((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const hasWriteScope = scopes.some((s) => s.startsWith("write:"));
  const hasAdminScope = scopes.includes("admin:*");
  const canSubmit =
    !busy && name.trim() && scopes.length > 0 && (!hasAdminScope || adminAck);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { name, scopes };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      const { data } = await api.post("/tenant/api-keys", payload);
      toast.success("API key created");
      setOpen(false);
      onCreated(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-api-key-btn"><Plus className="h-4 w-4" /> New API key</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="new-api-key-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> New API key</DialogTitle>
          <DialogDescription>Keys are shown once at creation. Store yours in a password manager.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input placeholder="Payroll production integration" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-api-key-name" />
          </div>
          <div className="space-y-3">
            <Label>Scopes</Label>
            {SCOPE_GROUPS.map((g) => (
              <div key={g.kind} className="space-y-2" data-testid={`scope-group-${g.kind}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.title}
                  </div>
                  {g.kind === "write" && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid="scope-group-write-badge">
                      <AlertTriangle className="h-3 w-3" /> Can mutate data
                    </span>
                  )}
                  {g.kind === "advanced" && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-700" data-testid="scope-group-advanced-badge">
                      <AlertTriangle className="h-3 w-3" /> Super-scope
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{g.hint}</p>
                <div className="space-y-1.5">
                  {g.scopes.map((s) => (
                    <div
                      key={s.id}
                      className={
                        "flex items-center gap-2 rounded-lg border p-2 hover:bg-muted/40 " +
                        (g.kind === "write" ? "border-amber-500/30 " : "") +
                        (g.kind === "advanced" ? "border-red-500/30 " : "")
                      }
                      data-testid={`new-api-key-scope-${s.id}`}
                    >
                      <Checkbox id={`api-key-scope-${s.id}`} checked={scopes.includes(s.id)} onCheckedChange={() => toggleScope(s.id)} />
                      <Label htmlFor={`api-key-scope-${s.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-xs text-muted-foreground font-mono">{s.id}</div>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {hasWriteScope && !hasAdminScope && (
              <Alert variant="default" className="border-amber-500/40 bg-amber-500/5" data-testid="write-scope-warning">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-900">
                  This key will be able to change your data. Store it in a secrets manager
                  and rotate it on employee offboarding.
                </AlertDescription>
              </Alert>
            )}
            {hasAdminScope && (
              <Alert variant="destructive" data-testid="admin-scope-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-semibold">You are granting full admin access.</div>
                  <div className="mt-1 text-xs">
                    This key can read and write everything in your workspace, including
                    directory data and approved payroll hours. Prefer narrower scopes.
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Checkbox id="admin-scope-ack" checked={adminAck} onCheckedChange={(v) => setAdminAck(!!v)} data-testid="admin-scope-ack" />
                    <Label htmlFor="admin-scope-ack" className="cursor-pointer text-xs">
                      I understand — grant full admin access to this key.
                    </Label>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
          <div className="space-y-2">
            <Label>Expires (optional)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="new-api-key-expiry" />
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="new-api-key-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealKeyDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [data]);
  if (!data) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.key); setCopied(true); toast.success("Copied"); }
    catch { toast.error("Copy failed — write it down"); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reveal-api-key-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-secondary" /> "{data.name}" — save this key now</DialogTitle>
          <DialogDescription>This is the only time you'll see the full key. If you lose it, revoke and create a new one.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border bg-muted/30 p-6">
          <div className="break-all font-mono text-sm tracking-tight text-primary" data-testid="revealed-api-key">{data.key}</div>
        </div>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Anyone with this key can access {data.scopes?.join(", ") || "your data"} on{" "}
            <code>/api/v1/*</code>. Treat it like a password.
          </AlertDescription>
        </Alert>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="copy-api-key-btn">
            <Copy className="mr-1 h-4 w-4" /> {copied ? "Copied" : "Copy key"}
          </Button>
          <Button onClick={onClose} data-testid="reveal-api-key-done">I've saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({ k, onClose, onConfirm }) {
  if (!k) return null;
  return (
    <Dialog open={!!k} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="api-key-revoke-dialog">
        <DialogHeader>
          <DialogTitle>Revoke "{k.name}"?</DialogTitle>
          <DialogDescription>
            Any integration using this key will immediately stop working. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => onConfirm(k)} data-testid="api-key-revoke-confirm">Revoke key</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
